<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <strong>Per movie tag</strong>
                    <span class="text-muted ml-2">— Brightfish preshow admissions (sales house BF) per cinema year, split per Movix target-pack tag</span>
                </div>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!rows.length" class="box">
            <div class="box-content text-center text-muted py-4">No Brightfish admissions found.</div>
        </div>

        <div v-else class="box">
            <div class="box-content">
                <div class="d-flex align-items-center mb-2">
                    <heading upper="Admissions per cinema year" :lower="rows.length + ' cinema years, sales house BF'" border class="flex-grow-1"/>
                    <div class="btn-group btn-group-sm ml-3">
                        <button type="button" class="btn" :class="mode === 'admissions' ? 'btn-secondary' : 'btn-outline-secondary'" @click="mode = 'admissions'">Admissions</button>
                        <button type="button" class="btn" :class="mode === 'pct' ? 'btn-secondary' : 'btn-outline-secondary'" @click="mode = 'pct'">% of year</button>
                        <button type="button" class="btn" :class="mode === 'movies' ? 'btn-secondary' : 'btn-outline-secondary'" @click="mode = 'movies'"># Movies</button>
                    </div>
                </div>
                <table class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th class="shx-th-100">Cinema year</th>
                            <th class="text-right shx-th-80"># Weeks</th>
                            <th class="text-right">Preshow (BF)</th>
                            <th v-for="tag in tags" :key="tag || 'none'" class="text-right">
                                <span :class="{ 'text-muted': !tag }">{{ tagLabel(tag) }}</span>
                            </th>
                            <th class="shx-th-100"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.year">
                            <td>
                                <router-link :to="'/admissions/movie-tag/' + row.year">{{ row.year }}</router-link>
                            </td>
                            <td class="text-right">{{ row.weeks }}</td>
                            <td class="text-right"><strong>{{ formatInt(row.admissions) }}</strong></td>
                            <td v-for="tag in tags" :key="tag || 'none'" class="text-right">
                                {{ cell(row, tag) }}
                            </td>
                            <td>
                                <router-link :to="'/admissions/movie-tag/' + row.year"
                                             class="btn btn-sm btn-outline-secondary">
                                    Detail
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td class="text-right"><strong>{{ totalWeeks }}</strong></td>
                            <td class="text-right"><strong>{{ formatInt(totalAdmissions) }}</strong></td>
                            <td v-for="tag in tags" :key="tag || 'none'" class="text-right">
                                <strong>{{ totalCell(tag) }}</strong>
                            </td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
                <p class="text-muted small mt-2 mb-0">
                    A cinema year runs from week C01 to the last cinema week (Wednesday to Tuesday).
                    Only rows with <code>saleshouse = BF</code> count. The tag is the Movix target pack of the movie
                    (<code>movix_history.movie_tag</code>); <em>{{ tagLabel(null) }}</em> collects titles without a
                    Movix id, without a history row or without a tag. Tags exist since cinema year {{ bucket.first_tag_year }}
                    (from 2023C50); earlier years are not reported. The historic <code>v2-adolescent</code>
                    spelling is counted as <code>v2-adolescents</code>.
                    Same data as JSON: <code>GET /api/v1/admissions/movie-tag</code>.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';

export default {
    name: 'AdmissionsMovieTagYears',
    components: {
        Heading,
    },
    data() {
        return {
            loading: true,
            bucket: {},
            rows: [],
            mode: 'admissions',
        };
    },
    computed: {
        tags() {
            return this.bucket.tags || [];
        },
        totalWeeks() {
            return this.rows.reduce((sum, r) => sum + (r.weeks || 0), 0);
        },
        totalAdmissions() {
            return this.rows.reduce((sum, r) => sum + (r.admissions || 0), 0);
        },
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            axios
                .get('/admissions/movie-tag')
                .then(response => {
                    this.bucket = response.data.data.bucket || {};
                    this.rows = response.data.data.rows || [];
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        tagOf(row, tag) {
            return (row.tags || []).find(t => (t.movie_tag || null) === (tag || null));
        },
        cell(row, tag) {
            const t = this.tagOf(row, tag);
            if (!t) {
                return '—';
            }
            if (this.mode === 'pct') {
                return t.pct !== null ? t.pct + '%' : '—';
            }
            if (this.mode === 'movies') {
                return t.movies;
            }
            return this.formatInt(t.admissions);
        },
        totalCell(tag) {
            const admissions = this.rows.reduce((sum, r) => sum + ((this.tagOf(r, tag) || {}).admissions || 0), 0);
            if (this.mode === 'pct') {
                return this.pct(admissions, this.totalAdmissions);
            }
            if (this.mode === 'movies') {
                return this.rows.reduce((sum, r) => sum + ((this.tagOf(r, tag) || {}).movies || 0), 0);
            }
            return this.formatInt(admissions);
        },
        tagLabel(tag) {
            return tag || 'no tag';
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
